import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  TextField,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import { useHistory } from "react-router-dom";
import { getBomInventory } from "./bomService";
import { IonContent, IonPage } from "@ionic/react";
import { styled } from "@mui/material/styles";
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(4),
  borderRadius: 12,
  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
  backdropFilter: "blur(4px)",
  "-webkit-backdrop-filter": "blur(4px)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  padding: theme.spacing(2),
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  color: theme.palette.common.white,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  "& .MuiTableCell-root": {
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.primary.lighter,
    cursor: "pointer",
  },
}));

const ManageBOMTable: React.FC = () => {
  const history = useHistory();
  const [products, setProducts] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const term = search.toLowerCase();
    const filteredList = products.filter((p) =>
      p.name.toLowerCase().includes(term)
    );
    setFiltered(filteredList);
  }, [search, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await getBomInventory();
      setProducts(res);
      setFiltered(res);
    } catch (err) {
      setError("Failed to fetch products");
      console.error("Failed to fetch products:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (loading) return <Typography sx={{ m: 4 }}>Loading...</Typography>;
  if (error) return <Typography color="error" sx={{ m: 4 }}>{error}</Typography>;

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <StyledCard>
          <HeaderBox>
            <BusinessCenterIcon fontSize="large" />
            <Typography variant="h5" component="h1">
              Product Bill of Materials
            </Typography>
          </HeaderBox>
          <CardContent>
            <Box mb={2} display="flex" justifyContent="flex-end">
              <TextField
                size="small"
                label="Search Product"
                value={search}
                fullWidth
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Paper elevation={3}>
              <Table>
                <StyledTableHead>
                  <TableRow>
                    <TableCell>S/N</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product, idx) => (
                      <StyledTableRow key={product.id}>
                        <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<SettingsIcon />}
                            onClick={() => {
                              history.push(
                                `/folder/bom-manage/${product.product_id}`,
                                { product }
                              );
                            }}
                          >
                            Manage BOM
                          </Button>
                        </TableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </Table>
            </Paper>

            <TablePagination
              component="div"
              count={filtered?.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </CardContent>
        </StyledCard>
      </IonContent>
    </IonPage>
  );
};

export default ManageBOMTable;
